/**
 * 네이버 클립 수익금 제출 — 화면 공용 계약 (2026-09-22 · 기획 docs/ops/naver-revenue-submission-plan.md)
 *
 * 서버(마이그레이션 20260923120000_naver_revenue_submission.sql)와 이름·형태를 맞춘다.
 *   · 카드 = naver_revenue_submission 한 행(채널 × 정산월). 정산월(payout_month) = 발생월(revenue_month) + 2.
 *   · 흐름: 캡처 업로드(비공개 버킷 naver-revenue) → naver_revenue_attach → Edge naver-revenue-ocr → naver_revenue_submit → 검사 R1~R8
 *           → auto_ok | flagged → 어드민 admin_naver_revenue_decide → confirmed | rejected.
 *   · 제출 창(대표 결정 2026-10-08·09 · 마이그레이션 20261008200000·20261009030000): 매월 9일 00:10 카드 열림 → 15일 23:59 마감(창 안에서는 어떤 제재도 없음) → 16일 0시부터 올릴 때까지 앱 안 전면 게이트
 *     (my_naver_revenue_gate — hard_deadline_at 을 지났으면 시각으로 바로 잠기고 rejected 카드도 포함). 게이트는 정산월 202610 부터(policy_from).
 *     **미지급(forfeited)은 약관 제28조 8~11항 시행(2026-11-01)에 맞춰 2026년 11월 발생분(정산월 202701)부터**(forfeit_from) — 카드의 forfeit_applies 로 문구가 갈린다.
 *   · 캡처 양식 강제(R9 · 대표 지시 2026-10-08): 네이버 「정산 완료 내역」 표의 해당 월 줄이 「정산 완료」인 캡처만 받는다. 자동 읽기가 screen·row_found·row_status·carried_over 를
 *     돌려주고, 화면(captureProblem)이 그 자리에서 돌려보내며 서버 naver_revenue_submit 이 같은 조건으로 접수를 거부한다.
 *   · 이월(2026-10-10 제이 의견 채택 · 대표 「제이 스레드에 맞춰서」): 5만원 미만으로 네이버가 이월한 달도 **그 달 금액 그대로** 낸다. 줄이 없으면 월별 그래프의 그 달 금액 화면(이월 안내 필요)을
 *     받고(carriedInfo 안내 한 줄), 다음 달 합산 지급액에서는 서버가 지난달 이월분을 빼도록 R10 으로 제안한다. 종전 「이월됐어요」 0원 제출은 폐지.
 *
 *   · 2026-10-14(제이 확인 · 대표 「1~3번 구현해서 14일에 배포해」 · 마이그레이션 20261014114612·20261014130000): 이월 달 그래프에서 다른 달 막대를 고른 캡처는
 *     wrong_bar 로 돌려보낸다(그 달 막대를 눌러 뜬 금액만). 확정·정정·미지급·되살리기·대행 입력은 확정 담당자(규칙 confirmers = 제이)만 — RPC.canConfirm.
 *
 * 🔒 규칙 상수(마감일·배수)는 여기 두지 않는다 — 서버 app_config.naver_revenue_rules 가 원본이고 화면은 서버가 준 값만 보여 준다.
 * 🔒 크리에이터 화면에는 검사 규칙 이름(R1~R8)·단가·중앙값을 보여 주지 않는다 — 「검토 중」으로만. 어드민만 flagLabel 을 쓴다.
 */
import { tt } from "../i18n/tt";
import { lazyI18n } from "../i18n/lazyText";

export const NAVER_REVENUE_BUCKET = "naver-revenue";
export const NAVER_REVENUE_OCR_FUNCTION = "naver-revenue-ocr";

export const RPC = Object.freeze({
  myCards: "my_naver_revenue_cards",
  myGate: "my_naver_revenue_gate",
  attach: "naver_revenue_attach",
  submit: "naver_revenue_submit",
  withdraw: "naver_revenue_withdraw", // 2026-10-09 — 마감 전 되돌려 다시 올리기
  adminBoard: "admin_naver_revenue_board",
  adminGrid: "admin_naver_revenue_grid",
  adminDecide: "admin_naver_revenue_decide",
  adminUpsert: "admin_naver_revenue_upsert",
  adminLog: "admin_naver_revenue_log",
  adminRules: "admin_naver_revenue_rules",
  adminSetRules: "admin_set_naver_revenue_rules",
  adminConfirmedAmounts: "admin_naver_revenue_confirmed_amounts",
  myHistory: "my_naver_revenue_history",
  canConfirm: "naver_revenue_can_confirm", // 2026-10-14 — 확정·정정·미지급·되살리기·대행 입력은 확정 담당자(제이)만
});

/**
 * 캡처 안내 — 네이버 「정산 완료 내역」 화면(정산월 / 집계월 · 지급액 / 상태) 을 그대로 찍어 올리게 한다.
 * 네이버 화면의 「집계월」이 우리 발생월(revenue_month)이고 「정산월」은 그보다 2달 뒤다(예: 정산월 8월 / 집계월 2026. 6.).
 */
export const CAPTURE_GUIDE = lazyI18n(() => Object.freeze({
  path: ["클립 크리에이터 앱", "내 정보", "수익", "정산 완료 내역"],
  title: tt("a.95a7c866", "정산 완료 내역"),
  columns: ["정산월 / 집계월", "지급액 / 상태"],
  rules: [
    "상태가 「정산 완료」인 줄만 받아요 — 「지급 예정」「지급 산정 중」이면 아직 확정 전이라 받을 수 없어요",
    "정산월 · 집계월 · 지급액 · 상태가 한 화면에 모두 보이게 찍어요 — 월별 수익 그래프나 「정산 현황」 요약은 받지 않아요(5만원 미만으로 이월돼 줄이 없는 달만 그래프의 그 달 금액 화면을 받아요)",
    "금액은 가리지 마세요 — 가려진 금액은 읽을 수 없어 운영팀 확인으로 넘어가요",
    "연도 선택이 보이면 그대로 두고, 해당 달 줄이 들어가게 찍어요",
    "계좌번호·주민등록번호가 함께 보이면 가리고 올려 주세요 — 금액·기간·채널명만 보이면 돼요",
  ],
  amountLabel: "지급액",
  statusLabel: "정산 완료",
  /** 표 그림 아래 한 줄 — 「지급 예정」이면 아직 확정 전 */
  notFinalNote: "해당 달 줄이 「지급 예정」「지급 산정 중」이면 아직 확정 전이에요. 「정산 완료」로 바뀐 뒤 찍어 주세요.",
  /** 5만원 미만 이월 — 그래도 그 달 금액 그대로 낸다(2026-10-10). 줄이 없으면 월별 그래프의 그 달 금액 화면, 다음 달 합산 지급액에서는 서버가 이월분을 뺀다(R10) */
  carriedNote: "그 달 수익이 5만원 미만이면 네이버가 다음 달로 이월해요. 그래도 그 달 금액 그대로 내요 — 정산 완료 내역에 그 달 줄이 없으면 월별 수익 그래프에서 그 달 금액과 이월 안내가 보이게 찍어 주세요. 합쳐서 지급된 다음 달에는 레이블리가 이월분을 빼고 접수해요.",
}));

/**
 * 제출 첫 단계 「상황 고르기」(2026-10-13 · 대표 결정 시안 1 + 시안 3 · docs/design/naver-revenue-settlement-place-variants.html B안).
 * 크리에이터가 네이버 클립 앱에서 그 달 줄이 어떻게 보이는지 고르면, 찍을 화면 그림 한 장과 체크리스트만 보여 준다.
 *   settled — 「정산 완료」 줄 → 정산 완료 내역 표 · pending — 「지급 예정」「지급 산정 중」 → 올리지 않고 기다림 ·
 *   carried — 줄이 없음(5만원 미만 이월) → 위쪽 이월 안내 카드 + 월별 그래프 그 달 툴팁.
 * 고른 답은 안내용이다 — 받을지 말지는 종전대로 자동 읽기(captureProblem)와 서버(naver_revenue_submit)가 정한다.
 */
export const SITUATION = Object.freeze({ settled: "settled", pending: "pending", carried: "carried" });

/** 번 달(YYYYMM) → 네이버 표에서 찾을 줄의 큰 글씨 숫자(두 달 뒤 — 돈을 받은 달) */
function rowNumberOf(revenueMonth) {
  const rm = String(revenueMonth || "");
  return rm.length === 6 ? parseInt(shiftMonth(rm, 2).slice(4, 6), 10) : null;
}

export function situationOptions(revenueMonth) {
  const month = monthLabel(revenueMonth);
  const row = rowNumberOf(revenueMonth);
  return [
    { key: SITUATION.settled, title: tt("a.nv13s01", "「정산 완료」라고 보여요"), chip: tt("a.nv13s02", "바로 낼 수 있어요"), tone: "ok", body: tt("a.nv14s03", "「{{v1}}월」 줄({{v0}}에 번 돈)을 찍어요", { v0: month, v1: row }) },
    { key: SITUATION.pending, title: tt("a.nv13s04", "「지급 예정」·「지급 산정 중」이에요"), chip: tt("a.nv13s05", "기다려요"), tone: "wait", body: tt("a.nv13s06", "네이버가 아직 확정하지 않았어요") },
    { key: SITUATION.carried, title: tt("a.nv14s07", "「{{v0}}월」 줄이 없어요", { v0: row }), chip: tt("a.nv13s08", "이월된 달"), tone: "ok", body: tt("a.nv14s09", "{{v0}} 수익이 5만원 미만이라 네이버가 다음 달로 넘긴 경우예요", { v0: month }) },
  ];
}

/** 상황별로 찍기 전에 할 일(체크리스트) */
export function situationChecklist(situation, revenueMonth) {
  const month = monthLabel(revenueMonth);
  const row = rowNumberOf(revenueMonth);
  const rm = String(revenueMonth || "");
  if (situation === SITUATION.carried) {
    return [
      tt("a.nv13c01", "클립 크리에이터 앱 › 내 정보 › 수익 열기"),
      tt("a.nv15c02", "월별 수익 현황에서 {{v0}} 막대를 눌러 금액 말풍선이 뜨게 하기", { v0: month }),
      tt("a.nv15c03", "위쪽 「최소 수익 5만원 미만」 카드와 금액 말풍선이 한 화면에 들어가게 찍기"),
    ];
  }
  return [
    tt("a.nv13c01", "클립 크리에이터 앱 › 내 정보 › 수익 열기"),
    tt("a.nv14c04", "「정산 완료 내역」에서 큰 글씨 「{{v0}}월」 줄 찾기(작은 글씨 「{{v1}}. {{v2}}.」 = {{v2}}월에 번 돈)", { v0: row, v1: rm.slice(0, 4), v2: parseInt(rm.slice(4, 6), 10) }),
    tt("a.nv14c05", "그 줄의 금액과 상태(정산 완료)가 한 화면에 들어가게 찍기"),
  ];
}

/**
 * QR 에 넣을 웹 주소의 앞부분 — 휴대폰 카메라로 열 수 있어야 하므로 http(s) 주소만. 앱(Capacitor) 안에서는 capacitor:// · http://localhost 라
 * 휴대폰에서 열리지 않으므로 실서비스 주소를 쓴다(2026-10-13 QA).
 */
export function publicWebOrigin(origin) {
  const o = String(origin || "");
  if (/^https?:\/\//.test(o) && !/^https?:\/\/localhost(:|$)/.test(o)) return o;
  return "https://laeebly.io";
}

/** 제출 창을 바로 여는 주소 — 알림·홈·PC 의 「휴대폰으로 이어서」 QR 이 모두 이 주소를 쓴다(정산 화면 B안) */
export function naverRevenueLink(cardId) {
  return cardId ? `/total?naver=${encodeURIComponent(cardId)}` : "/total?naver=open";
}

/** 다음 제출 카드가 열리는 날(매월 open_day 일, 기본 9일) — 「다음 제출은 11월 9일에 열려요」 */
export function nextOpenLabel(now = new Date(), openDay = 9) {
  const y = now.getFullYear();
  const m = now.getMonth();
  const next = now.getDate() < openDay ? new Date(y, m, openDay) : new Date(y, m + 1, openDay);
  return tt("a.nv13n01", "다음 제출은 {{v0}}월 {{v1}}일에 열려요", { v0: next.getMonth() + 1, v1: openDay });
}

/** 받을 수 있는 줄 상태 — 서버 naver_revenue_submit 의 허용 목록과 같아야 한다 */
export const ACCEPTED_ROW_STATUSES = Object.freeze(["정산 완료", "이월"]);

/** 허위 기재 책임 고지(v1) — 제출 직전 동의 필수. 서버 naver_revenue_submit(p_attested) · attested_at 에 남는다. 🔒 문안 변경은 법무 확인 뒤 version 을 올린다 */
export const LEGAL_ATTEST = Object.freeze({
  version: "v1",
  title: "제출 금액 확인",
  checkbox: "위 금액이 네이버 「정산 완료 내역」에 표시된 지급액과 같음을 확인합니다.",
  /**
   * 5만원 미만 이월 달(2026-10-13) — 「정산 완료 내역」에 그 달 줄이 없어 월별 그래프로 낸다. 확인 대상 화면만 바꾸고 고지 3줄·책임은 같다.
   * 서버에는 attest_version 「v1-carried」로 남아 어느 문장에 동의했는지 구분된다. 🔒 v1 과 함께 법무 확인 대상.
   */
  checkboxCarried: "위 금액이 네이버 클립 앱 「월별 수익 현황」에 표시된 그 달 수익 금액과 같음을 확인합니다(5만원 미만으로 이월된 달).",
  versionCarried: "v1-carried",
  lines: [
    "제출한 금액·캡처가 사실과 다르거나(허위 기재·누락·편집·조작) 다른 달·다른 채널의 화면을 올린 경우, 레이블리는 정산금 지급을 보류·환수하고 이용을 제한할 수 있습니다.",
    "고의로 적게 신고해 레이블리·영상권리사에 손해가 생기면 손해배상 등 민사상 책임과 관련 법령에 따른 형사상 책임을 질 수 있습니다.",
    "캡처는 정산 확인 목적으로만 보관·열람하며, 정산 분쟁 대응을 위해 관련 정산일로부터 5년간 보관합니다.",
  ],
});

export const STATUS = Object.freeze({
  open: "open",
  submitted: "submitted",
  autoOk: "auto_ok",
  flagged: "flagged",
  confirmed: "confirmed",
  rejected: "rejected",
  locked: "locked",
});

/** 어드민 전용 — 검사 규칙 칩 라벨. 크리에이터 화면에서 쓰지 말 것. */
export const FLAG_LABELS = Object.freeze({
  R1: "기간 불일치",
  R2: "채널 불일치",
  R3: "전월 동일값",
  R4: "단가 이탈",
  R5: "급변",
  R6: "사람이 고침",
  R7: "캡처 재사용",
  R8: "읽기 실패",
  R9: "화면·상태 불일치",
  R10: "이월 합산",
});

export const flagLabel = (code) => FLAG_LABELS[code] || code;

/** 'YYYYMM' → '2026년 8월' */
export function monthLabel(yyyymm) {
  if (!yyyymm || String(yyyymm).length !== 6) return "";
  const y = String(yyyymm).slice(0, 4);
  const m = parseInt(String(yyyymm).slice(4, 6), 10);
  return tt("a.861b1fa4", "{{v0}}년 {{v1}}월", { v0: y, v1: m });
}

/** 'YYYYMM' ± n */
export function shiftMonth(yyyymm, delta) {
  if (!yyyymm || String(yyyymm).length !== 6) return "";
  const y = parseInt(String(yyyymm).slice(0, 4), 10);
  const m = parseInt(String(yyyymm).slice(4, 6), 10) - 1 + delta;
  const d = new Date(Date.UTC(y, m, 1));
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function formatKRW(n) {
  if (n === null || n === undefined || n === "" || Number.isNaN(Number(n))) return "-";
  return tt("a.f7dda0b8", "{{v0}}원", { v0: Math.round(Number(n)).toLocaleString("ko-KR") });
}

export function formatViews(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "-";
  return tt("a.4cde4749", "{{v0}}회", { v0: Number(n).toLocaleString("ko-KR") });
}

/** KST 'M월 D일 HH:MM' */
export function formatDeadline(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const h = kst.getUTCHours();
  const m = kst.getUTCMinutes();
  const v = { v0: kst.getUTCMonth() + 1, v1: kst.getUTCDate() };
  // 서비스 말로(2026-10-09): 00:00 → 「0시」(잠금 16일 0시), 23:59 → 「자정」(마감 15일 자정). 서버 naver_revenue_fmt_kst 와 같은 규칙.
  if (h === 0 && m === 0) return tt("a.a7213ba4", "{{v0}}월 {{v1}}일 0시", v);
  if (h === 23 && m === 59) return tt("a.6d8f23fb", "{{v0}}월 {{v1}}일 자정", v);
  return tt("a.488e1343", "{{v0}}월 {{v1}}일 {{v2}}", { ...v, v2: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` });
}

/**
 * 크리에이터 카드 상태 → 화면 뷰모델. 규칙 이름·단가는 절대 넣지 않는다.
 *   kind: request | late | overdue | reviewing | confirmed | rejected | done
 */
/** 잠금(게이트) 상태인가 — 서버 my_naver_revenue_gate 와 같은 규칙: overdue 표시가 있거나 hard_deadline_at 을 지난 open·rejected 카드 */
export function isCardLocked(card, now = new Date()) {
  if (!card || !(card.status === STATUS.open || card.status === STATUS.rejected)) return false;
  if (card.overdue_at) return true;
  if (!card.hard_deadline_at) return false;
  const hardMs = new Date(card.hard_deadline_at).getTime();
  return !Number.isNaN(hardMs) && now.getTime() >= hardMs;
}

/**
 * 네이버 「정산 완료 내역」에서 찍어야 할 줄의 큰 글씨(= 돈을 받은 달 = 우리 정산월) — 「10월」 (2026-10-14 · 대표 결정 B안).
 * 네이버 표는 큰 글씨가 돈을 받은 달, 작은 글씨가 번 달이라 「8월 수익」이라고만 하면 큰 글씨 「8월」 줄(6월에 번 돈)을 올린다.
 */
export function naverRowMonth(card) {
  const pm = String(card?.payout_month || "");
  if (pm.length !== 6) return "";
  return tt("a.nv14r01", "{{v0}}월", { v0: parseInt(pm.slice(4, 6), 10) });
}

/** 「8월에 번 돈 = 네이버 앱 「10월」 줄」 — 카드·상자·잠금 화면·제출 창이 같은 문장을 쓴다(B안: 우리 이름은 그대로 · 네이버 말을 옆에) */
export function naverRowEquation(card) {
  const rm = String(card?.revenue_month || "");
  const row = naverRowMonth(card);
  if (rm.length !== 6 || !row) return null;
  return {
    earned: tt("a.nv14r02", "{{v0}}월에 번 돈", { v0: parseInt(rm.slice(4, 6), 10) }),
    row: tt("a.nv14r03", "네이버 앱 「{{v0}}」 줄", { v0: row }),
  };
}

export function creatorCardView(card, now = new Date()) {
  if (!card) return null;
  const nowMs = now.getTime();
  const deadlineMs = card.deadline_at ? new Date(card.deadline_at).getTime() : null;
  const status = card.status;
  const month = monthLabel(card.revenue_month);
  if (status === STATUS.open || status === STATUS.rejected) {
    const view = openCardView(card, now, nowMs, deadlineMs, status, month);
    return view ? { ...view, rowEq: naverRowEquation(card) } : view;
  }
  return settledCardView(card, now, status);
}

/** 낼 것(open · rejected) 카드의 문장 — creatorCardView 가 「8월에 번 돈 = 네이버 앱 「10월」 줄」(rowEq)을 붙인다 */
function openCardView(card, now, nowMs, deadlineMs, status, month) {
  if (isCardLocked(card, now)) {
    // 잠금 — 올릴 때까지 레이블리 이용 제한. 「미지급」은 forfeited(약관 적용 회차에서 크론이 세움)일 때만 말한다.
    const base = card.forfeited
      ? tt("a.21862b28", "마감이 지나 이 달 정산은 지급되지 않아요. 지금 올려 주시면 레이블리를 다시 이용할 수 있어요.")
      : tt("a.7c41d2e9", "올릴 때까지 레이블리 이용이 제한돼요. 지금 올려 주시면 바로 다시 이용할 수 있어요.");
    const isRejected = status === STATUS.rejected;
    return {
      kind: "overdue",
      tone: "error",
      title: isRejected
        ? tt("a.8fbc9b2e", "{{v0}} 네이버 클립 수익을 다시 올려 주세요", { v0: month })
        : tt("a.e3ea0158", "{{v0}} 네이버 클립 수익이 제출되지 않았어요", { v0: month }),
      body: isRejected && card.creator_message ? `${card.creator_message} ${base}` : base,
      cta: tt("a.e5cabd6b", "지금 올리기"),
    };
  }
  if (status === STATUS.rejected) {
    return {
      kind: "rejected",
      tone: "warning",
      title: tt("a.8fbc9b2e", "{{v0}} 네이버 클립 수익을 다시 올려 주세요", { v0: month }),
      body: card.creator_message || tt("a.11bb4f45", "운영팀이 확인 요청을 남겼어요."),
      cta: tt("a.88e84b79", "다시 올리기"),
    };
  }
  if (deadlineMs !== null && nowMs >= deadlineMs) {
    let body;
    if (card.hard_deadline_at) {
      body = tt("a.5b8e3f10", "{{v0}}부터는 올릴 때까지 레이블리 이용이 제한돼요.", { v0: formatDeadline(card.hard_deadline_at) });
      if (card.forfeit_applies) body += ` ${tt("a.c2f7a6d3", "그 달 정산도 지급되지 않아요.")}`;
    } else {
      body = tt("a.1a988748", "지금 올려 주시면 이번 정산에 반영돼요. 늦으면 다음 달 정산으로 미뤄질 수 있어요.");
    }
    return {
      kind: "late",
      tone: "warning",
      title: tt("a.10553cfb", "{{v0}} 네이버 클립 수익을 아직 못 받았어요", { v0: month }),
      body,
      cta: tt("a.2a044894", "캡처 올리기"),
    };
  }
  return {
    kind: "request",
    tone: "brand",
    title: tt("a.4ec6f518", "{{v0}} 네이버 클립 수익을 알려 주세요", { v0: month }),
    body: tt("a.nv14k01", "마감 {{v0}} · 네이버 클립 앱 「{{v1}}」 줄을 캡처해 올려 주세요. 어떻게 찍는지는 다음 단계에서 알려 드려요.", { v0: formatDeadline(card.deadline_at), v1: naverRowMonth(card) }),
    cta: tt("a.2a044894", "캡처 올리기"),
  };
}

/** 낸 뒤(확정 · 심사 중) 카드의 문장 */
function settledCardView(card, now, status) {
  if (status === STATUS.confirmed || status === STATUS.locked) {
    return {
      kind: "confirmed",
      tone: "success",
      title: tt("a.69d865df", "{{v0}} 네이버 클립 수익 {{v1}} 확정", { v0: monthLabel(card.revenue_month), v1: formatKRW(card.confirmed_amount) }),
      body: tt("a.21fe6ab9", "정산에 반영돼요. 고마워요!"),
      cta: null,
    };
  }
  // submitted · auto_ok · flagged — 크리에이터에겐 전부 「검토 중」. 마감 전이면 「다시 올리기」로 되돌릴 수 있다(2026-10-09)
  const withdrawable = canWithdraw(card, now);
  return {
    kind: "reviewing",
    tone: "neutral",
    title: tt("a.e5626212", "{{v0}} 네이버 클립 수익 {{v1}} 심사 중", { v0: monthLabel(card.revenue_month), v1: formatKRW(card.submitted_amount) }),
    body: tt("a.4cd238ef", "운영팀이 확인하면 확정 알림을 보내 드려요."),
    cta: null,
    canWithdraw: withdrawable,
    withdrawLabel: withdrawable ? tt("a.88e84b79", "다시 올리기") : null,
    withdrawHint: withdrawable ? tt("a.5f0a9c3d", "{{v0}}까지는 되돌려 새 캡처를 올릴 수 있어요.", { v0: formatDeadline(card.deadline_at) }) : null,
  };
}

/**
 * 마감 전 「되돌려 다시 올리기」가 되는 카드인가 — 서버 naver_revenue_withdraw 와 같은 규칙(🔒):
 *   본인 카드(source creator) · 상태 submitted/auto_ok/flagged(운영팀 확정 전) · 마감(deadline_at) 전.
 */
export function canWithdraw(card, now = new Date()) {
  if (!card || card.source === "backfill") return false;
  if (![STATUS.submitted, STATUS.autoOk, STATUS.flagged].includes(card.status)) return false;
  if (!card.deadline_at) return false;
  const ms = new Date(card.deadline_at).getTime();
  return !Number.isNaN(ms) && now.getTime() < ms;
}

/** 되돌리기 — 카드가 open 으로 돌아가고 캡처·제출값이 비워진다. 그 뒤 시트로 새 캡처를 올린다 */
export async function withdrawSubmission(client, { submissionId }) {
  const { data, error } = await client.rpc(RPC.withdraw, { p_id: submissionId });
  if (error) throw new Error(error.message || "withdraw failed");
  return data;
}

/** 카드 목록 중 「지금 할 일」(open · rejected) 만 — 홈 카드에 띄울 것 */
export function actionableCards(cards) {
  return (cards || []).filter((c) => c.status === STATUS.open || c.status === STATUS.rejected);
}

/** 어드민 대기열 상태 라벨 */
export const ADMIN_STATUS_META = Object.freeze({
  open: { label: "미제출", scheme: "gray" },
  submitted: { label: "검사 중", scheme: "blue" },
  auto_ok: { label: "자동 통과", scheme: "green" },
  flagged: { label: "검토 필요", scheme: "orange" },
  confirmed: { label: "확정", scheme: "brand" },
  rejected: { label: "반려", scheme: "red" },
  locked: { label: "잠김", scheme: "purple" },
});
export const adminStatusMeta = (s) => ADMIN_STATUS_META[s] || { label: s || "-", scheme: "gray" };

/** 어드민 진행 현황 한 줄: 13채널 중 제출 11 · 자동통과 8 · 검토 필요 3 · 미제출 2 */
export function summarizeBoard(rows) {
  const list = rows || [];
  const count = (pred) => list.filter(pred).length;
  return {
    total: list.length,
    submitted: count((r) => r.status !== STATUS.open),
    autoOk: count((r) => r.status === STATUS.autoOk),
    flagged: count((r) => r.status === STATUS.flagged),
    confirmed: count((r) => r.status === STATUS.confirmed || r.status === STATUS.locked),
    rejected: count((r) => r.status === STATUS.rejected),
    open: count((r) => r.status === STATUS.open),
    forfeited: count((r) => r.forfeited),
  };
}

/** 파일 sha256(hex). Web Crypto 없으면 null — 서버 R7 은 null 이면 건너뛴다. */
export async function sha256Hex(file) {
  try {
    if (!file || typeof window === "undefined" || !window.crypto?.subtle) return null;
    const buf = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

const EXT_BY_MIME = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/heic": "heic", "image/heif": "heif" };
export const ACCEPTED_MIME = Object.keys(EXT_BY_MIME);
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** 버킷 경로 규약: submissions/<submission_id>/<sha 또는 time>.<ext> — 서버 naver_revenue_attach 가 접두어를 검사한다 */
export function buildScreenshotPath(submissionId, file, sha) {
  const ext = EXT_BY_MIME[file?.type] || (file?.name || "").split(".").pop()?.toLowerCase() || "jpg";
  const stem = sha || String(Date.now());
  return `submissions/${submissionId}/${stem}.${ext}`;
}

export function validateScreenshotFile(file) {
  if (!file) return tt("a.192fd6e7", "파일을 골라 주세요.");
  if (!ACCEPTED_MIME.includes(file.type)) return tt("a.316dbc4a", "이미지 파일(JPG·PNG·WEBP·HEIC)만 올릴 수 있어요.");
  if (file.size > MAX_FILE_BYTES) return tt("a.378106b0", "10MB 이하 이미지만 올릴 수 있어요.");
  return null;
}

/**
 * 업로드 → 경로 등록 → 자동 읽기. 실패해도 경로 등록까지 됐으면 { ocr: null, error } 로 돌려 화면이 직접 입력을 받게 한다.
 */
export async function uploadAndRead(client, { submissionId, file }) {
  const bad = validateScreenshotFile(file);
  if (bad) throw new Error(bad);
  const sha = await sha256Hex(file);
  const path = buildScreenshotPath(submissionId, file, sha);
  const { error: upErr } = await client.storage.from(NAVER_REVENUE_BUCKET).upload(path, file, { upsert: true, contentType: file.type });
  if (upErr) throw new Error(upErr.message || tt("a.5f970215", "업로드에 실패했어요. 다시 시도해 주세요."));
  const { error: attachErr } = await client.rpc(RPC.attach, { p_id: submissionId, p_path: path, p_sha256: sha });
  if (attachErr) throw new Error(attachErr.message || "attach failed");
  const { data, error } = await client.functions.invoke(NAVER_REVENUE_OCR_FUNCTION, { body: { submission_id: submissionId } });
  if (error || !data?.ok) {
    return { path, sha, ocr: null, error: data?.error || error?.message || "ocr failed" };
  }
  return { path, sha, ocr: data.ocr || null, error: null };
}

/**
 * 자동 읽기 응답의 양식 판정 값(2026-10-08). 금액과 무관하게 뽑는다 — 금액을 못 읽어도 「받을 수 없는 캡처」는 먼저 가려야 하므로.
 *   { screen, rowFound, rowStatus, carriedOver } — 자동 읽기 자체가 실패(null)면 null.
 */
export function normalizeOcrMeta(ocr) {
  if (!ocr || typeof ocr !== "object") return null;
  const screen = typeof ocr.screen === "string" && ocr.screen ? ocr.screen : null;
  const rowStatus = typeof ocr.row_status === "string" && ocr.row_status ? ocr.row_status : null;
  const rowFound = ocr.row_found === true;
  // 읽은 줄의 번 달(작은 글씨 = 집계월) — 「8월 / 2026. 6.」 줄이면 202606 (2026-10-14 다른 달 줄 거부)
  const pf = typeof ocr.period_from === "string" ? ocr.period_from : "";
  const periodMonth = /^\d{4}-\d{2}/.test(pf) ? pf.slice(0, 4) + pf.slice(5, 7) : null;
  // 기간 끝 달(2026-10-14) — 그래프는 머리의 「2026. 5. ~ 2026. 10.」 같은 범위를 읽을 수 있어서, 한 달짜리 기간일 때만 막대 달로 본다(wrong_bar)
  const pt = typeof ocr.period_to === "string" ? ocr.period_to : "";
  const periodToMonth = /^\d{4}-\d{2}/.test(pt) ? pt.slice(0, 4) + pt.slice(5, 7) : null;
  return {
    screen,
    rowFound,
    rowStatus,
    periodMonth,
    periodToMonth,
    // 해당 월 줄이 「정산 완료」로 있으면 화면의 이월 안내는 다른 달 얘기 — 서버·Edge 와 같은 규칙
    carriedOver: rowStatus === "이월" || (ocr.carried_over === true && !(rowFound && rowStatus === "정산 완료")),
  };
}

/**
 * 「받을 수 없는 캡처」 판정 — 서버 naver_revenue_submit 의 거부 조건과 같은 순서·같은 규칙(🔒 한쪽만 바꾸지 말 것).
 *   kind: wrong_screen | not_final | row_missing → block:true (그 자리에서 다른 캡처를 받는다)
 *         (이월은 문제가 아니다 — 2026-10-10 부터 그 달 금액 그대로 받고 carriedInfo 로 안내만 한다)
 *   null → 문제 없음. meta 가 null(자동 읽기 실패)이면 null — 종전대로 직접 입력(R8, 운영팀이 캡처를 본다).
 */
export function captureProblem(meta, revenueMonth) {
  if (!meta) return null;
  const month = monthLabel(revenueMonth);
  // 월별 그래프는 「이월된 달」일 때만 받는다(그 달 금액 + 이월 안내가 보이는 화면) — 서버 naver_revenue_submit 과 같은 규칙
  // 2026-10-13: 그래프를 막을 때 「정산 완료 내역을 찍으라」만 하면 이월된 달(그 달 줄이 없다)은 따를 수 없다 → 이월 안내까지 함께 찍으라고 먼저 안내
  if (meta.screen === "monthly_chart" && !meta.carriedOver) {
    return {
      kind: "wrong_screen",
      block: true,
      title: tt("a.6d2b8e17", "이 캡처는 받을 수 없어요"),
      body: tt("a.7c1e9a42", "월별 수익 그래프예요. {{v0}} 수익이 5만원 미만이라 네이버가 다음 달로 넘긴(이월) 달이면, 그래프에서 {{v0}} 금액과 화면 위쪽의 이월 안내가 한 화면에 함께 보이게 다시 찍어 주세요. 이월된 달이 아니면 클립 크리에이터 앱 › 내 정보 › 수익 › 정산 완료 내역을 캡처해 주세요.", { v0: month }),
    };
  }
  if (meta.screen && meta.screen !== "settlement_history" && meta.screen !== "monthly_chart") {
    const what = meta.screen === "settlement_status"
        ? tt("a.a17d0c58", "「정산 현황」 요약 화면이에요.")
        : tt("a.f09b2d61", "네이버 「정산 완료 내역」 화면이 아니에요.");
    return {
      kind: "wrong_screen",
      block: true,
      title: tt("a.6d2b8e17", "이 캡처는 받을 수 없어요"),
      body: `${what} ${tt("a.b85c0f24", "클립 크리에이터 앱 › 내 정보 › 수익 › 정산 완료 내역을 열어 아래 그림처럼 찍어 주세요.")}`,
    };
  }
  // 2026-10-14 다른 달 줄(대표 결정 B안 · 제이 요청): 「정산 완료 내역」에서 번 달이 다른 줄(대개 큰 글씨가 그 달 이름인 「8월 / 2026. 6.」)을 읽었으면
  //   그 자리에서 돌려보낸다 — 서버 naver_revenue_submit 의 wrong_row 와 같은 규칙. 이미 정산이 끝난 달을 또 받는 것을 막는다.
  if (meta.screen === "settlement_history" && meta.periodMonth && revenueMonth && meta.periodMonth !== String(revenueMonth)) {
    const pm = meta.periodMonth;
    const bigOfRead = parseInt(shiftMonth(pm, 2).slice(4, 6), 10);
    const want = parseInt(shiftMonth(String(revenueMonth), 2).slice(4, 6), 10);
    const earned = parseInt(String(revenueMonth).slice(4, 6), 10);
    return {
      kind: "wrong_row",
      block: true,
      title: pm < String(revenueMonth) ? tt("a.nv14w01", "지난 정산 줄이에요") : tt("a.nv14w02", "다른 달 줄이에요"),
      body: tt("a.nv14w03", "올리신 줄은 「{{v0}}월 / {{v1}}. {{v2}}.」 — {{v2}}월에 번 돈이에요. {{v3}}월에 번 돈은 네이버 앱 「{{v4}}월」 줄에 있어요(큰 글씨는 돈을 받은 달이에요). 「{{v4}}월」 줄이 없다면 {{v3}}월 수익이 5만원 미만이라 이월된 거예요 — 월별 수익 현황에서 {{v3}}월 막대를 눌러 금액과 위쪽 「최소 수익 5만원 미만」 안내가 함께 보이게 찍어 주세요.", {
        v0: bigOfRead, v1: pm.slice(0, 4), v2: parseInt(pm.slice(4, 6), 10), v3: earned, v4: want,
      }),
    };
  }
  // 2026-10-14 이월 달 그래프에서 다른 달 막대(제이 확인 ②: 그 달 막대를 눌러 뜬 금액만) — 서버 naver_revenue_submit 의 wrong_bar 와 같은 규칙.
  //   읽은 기간이 한 달짜리일 때만 본다(그래프 머리의 범위를 읽은 경우는 막지 않는다).
  if (meta.screen === "monthly_chart" && meta.periodMonth && revenueMonth && meta.periodMonth !== String(revenueMonth)
      && (!meta.periodToMonth || meta.periodToMonth === meta.periodMonth)) {
    return {
      kind: "wrong_bar",
      block: true,
      title: tt("a.nv15w02", "다른 달 막대예요"),
      body: tt("a.nv15w01", "그래프에서 고른 막대는 {{v0}}월에 번 돈이에요. 월별 수익 현황에서 {{v1}}월 막대를 눌러 금액이 뜨게 한 뒤, 위쪽 「최소 수익 5만원 미만」 안내와 함께 다시 찍어 주세요.", {
        v0: parseInt(meta.periodMonth.slice(4, 6), 10), v1: parseInt(String(revenueMonth).slice(4, 6), 10),
      }),
    };
  }
  if (meta.rowStatus && !ACCEPTED_ROW_STATUSES.includes(meta.rowStatus)) {
    return {
      kind: "not_final",
      block: true,
      title: tt("a.0a93f7c1", "아직 확정 전 금액이에요"),
      body: tt("a.e4b1a0d6", "{{v0}} 줄 상태가 「{{v1}}」이에요. 네이버가 확정해 「정산 완료」로 바뀐 뒤 다시 찍어 올려 주세요(보통 매달 6~7일).", { v0: month, v1: meta.rowStatus }),
    };
  }
  if (meta.screen === "settlement_history" && meta.rowFound === false && !meta.carriedOver) {
    return {
      kind: "row_missing",
      block: true,
      title: tt("a.51c7e2a8", "{{v0}} 줄이 안 보여요", { v0: month }),
      body: tt("a.b754dfdf", "정산월·집계월·지급액·상태가 보이는 {{v0}} 줄이 한 화면에 들어가게 다시 찍어 주세요. 그 달 수익이 5만원 미만이라 줄이 없다면 월별 수익 그래프에서 {{v0}} 금액과 이월 안내가 보이는 화면을 올려 주세요.", { v0: month }),
    };
  }
  return null;
}

/**
 * 이월된 달 안내(2026-10-10 · 제이 의견 채택) — 「받을 수 없는 캡처」가 아니라 결과 단계에 붙는 한 줄.
 * 그 달 금액 그대로 받고, 서버는 validation.carried_over 로 남겨 다음 달 합산 지급액에서 빼도록(R10) 제안한다.
 */
export function carriedInfo(meta, revenueMonth) {
  if (!meta?.carriedOver) return null;
  return {
    title: tt("a.2f8d5a3b", "{{v0}}은 5만원 미만으로 이월됐어요", { v0: monthLabel(revenueMonth) }),
    body: tt("a.8e0cc2db", "그래도 레이블리는 이 달 금액 그대로 정산해요. 네이버가 다음 달에 합쳐서 줄 때는 이 금액을 빼고 접수해요."),
  };
}

export async function submitAmount(client, { submissionId, amount, edited, reason, attested, carried = false, attestVersion = LEGAL_ATTEST.version }) {
  if (!attested) throw new Error(tt("a.183f5a1a", "제출 금액 확인에 동의해 주세요."));
  const { data, error } = await client.rpc(RPC.submit, {
    p_id: submissionId, p_amount: amount, p_edited: !!edited, p_reason: reason || null, p_attested: true, p_attest_version: attestVersion || LEGAL_ATTEST.version,
    p_carried: !!carried,
  });
  if (error) throw new Error(error.message || "submit failed");
  return data;
}

export async function fetchMyCards(client) {
  const { data, error } = await client.rpc(RPC.myCards);
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function fetchMyGate(client) {
  const { data, error } = await client.rpc(RPC.myGate);
  if (error) throw error;
  return data && typeof data === "object" ? data : { gated: false, cards: [] };
}

/** 서명 URL(60초) — 권한은 storage 정책이 판정한다 */
export async function signedScreenshotUrl(client, path, expiresIn = 60) {
  if (!path) return null;
  const { data, error } = await client.storage.from(NAVER_REVENUE_BUCKET).createSignedUrl(path, expiresIn);
  if (error) return null;
  return data?.signedUrl || null;
}

/**
 * 업로드 파일(processedData: channel_name · channel_total_revenue 행들)에서 채널별 「채널 수익」을 모은다.
 *   revenue = 첫 행 값(기존 업로드 코드와 같은 규칙) · variants = 그 채널에 나타난 서로 다른 값의 수(2 이상이면 칸 밀림 의심)
 */
export function collectFileChannelRevenues(rows) {
  const map = new Map();
  (rows || []).forEach((r) => {
    const key = String(r?.channel_name || "").trim();
    if (!key) return;
    const rev = Number(r?.channel_total_revenue || 0);
    if (!map.has(key)) map.set(key, { channel_name: key, revenue: rev, values: new Set() });
    map.get(key).values.add(Math.round(rev));
  });
  return Array.from(map.values()).map(({ channel_name, revenue, values }) => ({ channel_name, revenue, variants: values.size }));
}

/**
 * 업로드 안전망(1단계) — 파일의 채널별 「채널 수익」을 확정 금액과 대조한다.
 *   guard=false(guard_from 이전 달) 면 통과. 확정 없음 · 금액 다름 · forfeited 면 거부 사유를 돌려준다.
 *   fileChannels: [{ channel_name, revenue }] (채널마다 1개, 첫 행 값)
 */
export function checkUploadAgainstConfirmed(confirmedPayload, fileChannels) {
  const guard = !!confirmedPayload?.guard;
  if (!guard) return { ok: true, problems: [] };
  const byName = new Map();
  (confirmedPayload.rows || []).forEach((r) => byName.set(String(r.channel_name || "").trim(), r));
  const problems = [];
  (fileChannels || []).forEach(({ channel_name, revenue }) => {
    const key = String(channel_name || "").trim();
    const row = byName.get(key);
    if (!row) { problems.push({ channel_name: key, reason: "no_card", message: tt("a.d9b0e076", "{{v0}}: 제출 카드가 없습니다(계약 채널이 아니거나 이 달 카드가 안 열렸어요)", { v0: key }) }); return; }
    if (row.forfeited) { problems.push({ channel_name: key, reason: "forfeited", message: tt("a.b670f2a1", "{{v0}}: 미제출 미지급(정책 ③) 채널입니다 — 이 달은 올릴 수 없어요", { v0: key }) }); return; }
    if (!(row.status === STATUS.confirmed || row.status === STATUS.locked) || row.amount === null || row.amount === undefined) {
      problems.push({ channel_name: key, reason: "not_confirmed", message: tt("a.438f7792", "{{v0}}: 아직 확정되지 않았어요({{v1}})", { v0: key, v1: adminStatusMeta(row.status).label }) });
      return;
    }
    if (Math.round(Number(revenue)) !== Math.round(Number(row.amount))) {
      problems.push({ channel_name: key, reason: "mismatch", message: tt("a.7ad37a4a", "{{v0}}: 파일 {{v1}} ≠ 확정 {{v2}}", { v0: key, v1: formatKRW(revenue), v2: formatKRW(row.amount) }) });
    }
  });
  return { ok: problems.length === 0, problems };
}

/* ── 제출 내역(히스토리) — 정산 관리 모달 「네이버 클립 제출 내역」 (시안 B · 대표 선택 2026-09-22) ── */

/** 크리에이터 어휘의 상태 라벨. 🔒 규칙 코드·단가 없음. kind: reviewing | confirmed | rejected | open | forfeited | legacy */
export function historyStatusLabel(card) {
  if (!card) return { kind: "open", label: "", scheme: "gray" };
  if (card.source === "backfill") return { kind: "legacy", label: tt("a.b3ac6500", "이전 방식 제출"), scheme: "gray" };
  if (card.forfeited) return { kind: "forfeited", label: tt("a.e0611a57", "미제출 · 미지급"), scheme: "red" };
  switch (card.status) {
    case STATUS.confirmed:
    case STATUS.locked:
      return { kind: "confirmed", label: tt("a.df2a1bdc", "확정"), scheme: "brand" };
    case STATUS.rejected:
      return { kind: "rejected", label: tt("a.c399dee6", "다시 올려 주세요"), scheme: "orange" };
    case STATUS.submitted:
    case STATUS.autoOk:
    case STATUS.flagged:
      return { kind: "reviewing", label: tt("a.76eff707", "심사 중"), scheme: "blue" };
    default:
      return { kind: "open", label: card.overdue_at ? tt("a.f28a82ef", "마감 지남") : tt("a.89f927e4", "미제출"), scheme: card.overdue_at ? "red" : "gray" };
  }
}

/** 제출 금액 vs 정산에 반영된 확정 금액. same | differs | pending | none */
export function compareSubmittedVsConfirmed(card) {
  if (!card) return { kind: "none" };
  const sub = card.submitted_amount == null ? null : Number(card.submitted_amount);
  const conf = card.confirmed_amount == null ? null : Number(card.confirmed_amount);
  if (card.source === "backfill") return { kind: "legacy", confirmed: conf };
  if (conf == null) return { kind: sub == null ? "none" : "pending", submitted: sub };
  if (sub == null || Math.round(sub) === Math.round(conf)) return { kind: "same", submitted: sub, confirmed: conf };
  return { kind: "differs", submitted: sub, confirmed: conf, diff: conf - sub };
}

/** 이력 이벤트 → 사람 말. 크리에이터에게 보여 줄 종류만 남긴다 */
export function historyEventLabel(ev) {
  if (!ev) return null;
  const amt = ev.amount != null ? formatKRW(ev.amount) : null;
  switch (ev.action) {
    case "attach": return tt("a.80cd3487", "캡처를 올렸어요");
    case "ocr": return ev.ocr_amount != null ? tt("a.312becdc", "자동으로 {{v0}} 읽음", { v0: formatKRW(ev.ocr_amount) }) : tt("a.17f60cb3", "자동 읽기 실패 — 직접 입력");
    case "submit": return amt ? tt("a.ffda9725", "{{v0}} 제출 · 제출 금액 확인 동의", { v0: amt }) : tt("a.d6ed72d2", "제출");
    case "confirm": return amt ? tt("a.3442e9ca", "운영팀 확정 {{v0}}", { v0: amt }) : tt("a.72b16827", "운영팀 확정");
    case "reject": return ev.message ? tt("a.670ff260", "다시 올려 달라는 요청: {{v0}}", { v0: ev.message }) : tt("a.217a7ee2", "다시 올려 달라는 요청");
    case "correct": return amt ? tt("a.94c3bcd0", "확정 금액 정정 {{v0}}", { v0: amt }) : tt("a.9b9f0105", "확정 금액 정정");
    case "admin_upsert": return amt ? tt("a.1b6135c8", "운영팀이 대신 입력 {{v0}}", { v0: amt }) : tt("a.081cbc0d", "운영팀이 대신 입력");
    case "overdue": return tt("a.4f2c9b85", "마감까지 제출되지 않아 레이블리 이용 제한");
    case "forfeit": return tt("a.70aff750", "이 달 정산 미지급 처리");
    case "restore": return tt("a.5aaafc93", "미지급이 풀렸어요");
    case "reopen": return tt("a.4aa9c1b1", "다시 열림");
    case "withdraw": return tt("a.6e1b8d47", "다시 올리려고 제출을 되돌렸어요");
    default: return null;
  }
}

/** 이력 카드 목록에서 정산월 하나를 고른다(채널이 여럿이면 여러 장) */
export function cardsForPayoutMonth(cards, payoutMonth) {
  return (cards || []).filter((c) => String(c.payout_month) === String(payoutMonth));
}

export async function fetchMyHistory(client, months = 24) {
  const { data, error } = await client.rpc(RPC.myHistory, { p_months: months });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
